import React, { useEffect, useMemo, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import api from "./api";

function toDateOnly(value) {
  // Accepts: "2026-01-02" OR "2026-01-02T00:00:00Z" OR Date
  if (!value) return "";
  const s = String(value);

  // If already date-only, keep it
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // If ISO string with time, convert to LOCAL date-only
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function Scheduler() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  // zoom controls calendar size
  const [zoom, setZoom] = useState(0.95);

  const fetchAllEvents = async () => {
    setLoading(true);
    try {
      const [woRes, pmRes] = await Promise.all([
        api.get("/api/work-orders"),
        api.get("/api/pm-schedules"),
      ]);

      const woData = woRes.data;
      const pmData = pmRes.data;

      const workOrders = Array.isArray(woData)
        ? woData
        : woData?.work_orders || woData?.items || [];

      const pmSchedules = Array.isArray(pmData)
        ? pmData
        : pmData?.pm_schedules || pmData?.items || [];

      const woEvents = (workOrders || [])
        .filter((wo) => wo.scheduled_date)
        .map((wo) => ({
          id: `wo-${wo.id}`,
          title: wo.task || `WO #${wo.id}`,
          start: toDateOnly(wo.scheduled_date), // ✅ date-only local
          allDay: true,
          color: "#38bdf8",
          extendedProps: {
            kind: "wo",
            woId: wo.id,
            line: wo.line,
            machine: wo.machine,
            assignee: wo.assignee,
            type: wo.type,
            status: wo.status,
            lead_time: wo.lead_time,
            date_created: wo.date,
          },
        }));

      const pmEvents = (pmSchedules || [])
        .filter((pm) => pm.date)
        .map((pm) => ({
          id: `pm-${pm.id}`,
          title: pm.job_id || (pm.machine ? `PM - ${pm.machine}` : `PM #${pm.id}`),
          start: toDateOnly(pm.date), // ✅ date-only local
          allDay: true,
          color: "#22c55e",
          extendedProps: {
            kind: "pm",
            pmId: pm.id,
            line: pm.line,
            machine: pm.machine,
            assignee: pm.assignee,
            frequency: pm.frequency,
            tasks: pm.tasks,
            is_active: pm.is_active,
          },
        }));

      setEvents([...woEvents, ...pmEvents]);
    } catch (err) {
      console.error("Error loading scheduler events", err);
      setEvents([]);
      alert("❌ Failed to load scheduler data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllEvents();
    // eslint-disable-next-line
  }, []);

  const handleEventDrop = async (info) => {
    const kind = info.event.extendedProps.kind;

    if (kind === "pm") {
      info.revert();
      alert("PM dates are managed from PM Schedule (not by dragging).");
      return;
    }

    const woId = info.event.extendedProps.woId;

    // ✅ date-only local
    const newDate = toDateOnly(info.event.startStr);

    try {
      await api.put(`/api/work-orders/${woId}`, { scheduled_date: newDate });
    } catch (err) {
      console.error("Error rescheduling", err);
      alert("❌ Failed to reschedule work order");
      info.revert();
    }
  };

  const handleEventClick = (info) => {
    const { title, extendedProps } = info.event;
    const details = extendedProps || {};
    const kind = details.kind;

    if (kind === "pm") {
      alert(
        [
          "PM Job",
          `Title: ${title}`,
          `Line: ${details.line || "-"}`,
          `Machine: ${details.machine || "-"}`,
          `Assignee: ${details.assignee || "-"}`,
          `Frequency (months): ${details.frequency ?? "-"}`,
          `Active: ${details.is_active ? "Yes" : "No"}`,
          "",
          "Tasks:",
          details.tasks || "-",
        ].join("\n")
      );
    } else {
      alert(
        [
          `WO #${details.woId}`,
          `Task: ${title}`,
          `Line: ${details.line || "-"}`,
          `Machine: ${details.machine || "-"}`,
          `Assignee: ${details.assignee || "-"}`,
          `Type: ${details.type || "-"}`,
          `Status: ${details.status || "-"}`,
          `Created: ${details.date_created || "-"}`,
          `Lead Time: ${details.lead_time || "-"}`,
          `Scheduled Date: ${toDateOnly(info.event.startStr) || "-"}`,
        ].join("\n")
      );
    }
  };

  const calendarScaleStyle = useMemo(
    () => ({
      transform: `scale(${zoom})`,
      transformOrigin: "top left",
      width: `${100 / zoom}%`,
    }),
    [zoom]
  );

  return (
    <div className="main-content scheduler-page">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <h1 style={{ marginBottom: 6 }}>📅 Scheduler</h1>
          <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 0 }}>
            Blue events = Work Orders · Green events = PM jobs {loading ? " (loading...)" : ""}
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "#64748b" }}>Size</span>
          <input
            type="range"
            min="0.80"
            max="1.15"
            step="0.01"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
          <span style={{ fontSize: 12, color: "#64748b", width: 44, textAlign: "right" }}>
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </div>

      <div style={{ width: "100%", overflowX: "auto", marginTop: 14 }}>
        <div style={{ minWidth: 980 }}>
          <div style={calendarScaleStyle}>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"

              // ✅ Fix header buttons order + behavior
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "",
              }}

              // ✅ Fix "today" date issues
              timeZone="local"
              now={new Date()} // makes today correct even if server timezone differs

              editable
              events={events}
              eventDrop={handleEventDrop}
              eventClick={handleEventClick}

              height="auto"
              windowResizeDelay={150}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Scheduler;
